import React, { useEffect, useState } from "react";
import BaseModalAnimated from "../../components/modal/BaseModalAnimated";
import { rolsResource } from "../../helpers/configRoutes/backend_helper";

const RolModal = ({ modal, setModal }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const getRolModel = async () => {
            if (!modal?.rol?.id) return;

            setLoading(true);
            try {
                const response = await rolsResource("show", {}, modal.rol.id);

                if (response.status && response.rol) {
                    setData(response.rol);
                } else {
                    setData(null);
                }
            } catch (error) {
                console.error("Error loading role models:", error);
                setData(null);
            } finally {
                setLoading(false);
            }
        };

        getRolModel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modal.rol]);

    const modelsEntries = data?.models ? Object.entries(data.models) : [];
    const totalModels = modelsEntries.length;

    return (
        <BaseModalAnimated
            modal={modal}
            setModal={setModal}
            title={modal.title || "Rol Detail"}
        >
            <div className="w-full max-w-full bg-cs-bg rounded-2xl border border-cs-border overflow-hidden">
                {/* HEADER */}
                <div className="px-5 py-4 bg-cs-surface border-b border-cs-border flex items-start justify-between gap-3">
                    <div className="flex flex-col gap-1">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-cs-primary-dark">
                            Role overview
                        </span>
                        <p className="text-[10px] text-cs-muted">
                            {data?.description ||
                                modal?.rol?.description ||
                                "Review the modules and permissions assigned to this role."}
                        </p>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                        <span className="px-2 py-0.5 rounded-full bg-cs-primary-soft text-cs-primary-dark text-[9px]">
                            {loading
                                ? "Loading modules..."
                                : `${totalModels} modules configured`}
                        </span>
                    </div>
                </div>

                {/* BODY */}
                <div className="px-5 py-4 bg-cs-bg-alt max-h-[60vh] overflow-y-auto">
                    {loading ? (
                        // Skeleton loading
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="bg-cs-bg border border-cs-border rounded-2xl p-3 animate-pulse"
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="w-7 h-7 rounded-xl bg-cs-primary-soft" />
                                        <div className="flex-1 space-y-1">
                                            <div className="h-2.5 w-24 bg-cs-bg-alt rounded" />
                                            <div className="h-2 w-32 bg-cs-bg-alt rounded" />
                                        </div>
                                    </div>
                                    <div className="flex gap-1 flex-wrap">
                                        <div className="h-4 w-20 bg-cs-bg-alt rounded-full" />
                                        <div className="h-4 w-24 bg-cs-bg-alt rounded-full" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : !data || totalModels === 0 ? (
                        <div className="py-8 text-center text-[10px] text-cs-muted">
                            This role has no modules assigned yet.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {modelsEntries.map(([modelName, modelData]) => {
                                const permissions = modelData.permissions || [];
                                const hasTotalAccess = permissions.some((p) =>
                                    p.name.toLowerCase().includes("total access")
                                );

                                return (
                                    <div
                                        key={modelName}
                                        className="
                                            bg-cs-bg
                                            border border-cs-border
                                            rounded-2xl
                                            p-3
                                            flex flex-col gap-2
                                            shadow-sm
                                        "
                                    >
                                        {/* MODULE HEADER */}
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="
                                                        w-7 h-7 rounded-xl
                                                        bg-cs-primary-soft
                                                        flex items-center justify-center
                                                        text-[9px] font-semibold text-cs-primary-dark
                                                    "
                                                >
                                                    {modelName
                                                        .split(" ")
                                                        .map((w) => w[0])
                                                        .join("")
                                                        .slice(0, 3)
                                                        .toUpperCase()}
                                                </div>
                                                <div className="flex flex-col leading-tight">
                                                    <span className="text-[11px] font-semibold text-cs-dark">
                                                        {modelName}
                                                    </span>
                                                    <span className="text-[8px] text-cs-muted">
                                                        Module permissions summary
                                                    </span>
                                                </div>
                                            </div>

                                            {hasTotalAccess && (
                                                <span
                                                    className="
                                                        px-2 py-0.5 rounded-full
                                                        bg-cs-primary
                                                        text-white
                                                        text-[8px] font-medium
                                                        inline-flex items-center gap-1
                                                    "
                                                >
                                                    <span className="material-symbols-outlined text-[11px]">
                                                        workspace_premium
                                                    </span>
                                                    Total access
                                                </span>
                                            )}
                                        </div>

                                        {/* PERMISSIONS LIST */}
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {permissions.map((perm, idx) => {
                                                const label = perm.name || "";
                                                const lower = label.toLowerCase();

                                                let cls =
                                                    "bg-cs-primary-soft border-cs-primary-soft text-cs-primary-dark";
                                                if (lower.includes("view")) {
                                                    cls =
                                                        "bg-cs-bg-alt border-cs-border text-cs-muted";
                                                } else if (lower.includes("export")) {
                                                    cls =
                                                        "bg-cs-surface-alt border-cs-primary-soft text-cs-primary-dark";
                                                } else if (lower.includes("edit") || lower.includes("manage")) {
                                                    cls =
                                                        "bg-cs-primary-soft border-cs-primary text-cs-primary-dark";
                                                }

                                                return (
                                                    <span
                                                        key={idx}
                                                        className={`
                                                            px-2 py-0.5
                                                            rounded-full
                                                            text-[8px] leading-tight
                                                            border
                                                            ${cls}
                                                        `}
                                                    >
                                                        {label}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </BaseModalAnimated>
    );
};

export default RolModal;
